import React, { useState, useRef, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';

const OTP_COOLDOWN = 60;

export default function ProfileEmailField({ value, onChange, error, onVerified, verifiedEmail, originalEmail }) {
  const [otpStep, setOtpStep] = useState('idle');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);

  const isUnchanged = value?.trim() === originalEmail?.trim();
  const isVerified = isUnchanged || (verifiedEmail && verifiedEmail === value?.trim());

  useEffect(() => {
    return () => { if (cooldownRef?.current) clearInterval(cooldownRef?.current); };
  }, []);

  function startCooldown() {
    setCooldown(OTP_COOLDOWN);
    cooldownRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(cooldownRef?.current); return 0; }
        return c - 1;
      });
    }, 1000);
  }

  function handleEmailChange(e) {
    onChange(e);
    if (otpStep === 'verified' || otpStep === 'sent') {
      setOtpStep('idle');
      setOtpCode('');
      setOtpError('');
      onVerified('');
    }
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email?.trim() || '');
  }

  async function handleSendOtp() {
    if (!value || !isValidEmail(value)) {
      setOtpError('Please enter a valid email address first.');
      return;
    }
    setOtpStep('sending');
    setOtpError('');
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke('send-email-otp', {
        body: { email: value?.trim(), action: 'send' }
      });
      if (fnError || data?.error) {
        setOtpError(fnError?.message || data?.error || 'Failed to send code. Please try again.');
        setOtpStep('error');
      } else {
        setOtpStep('sent');
        startCooldown();
      }
    } catch (err) {
      setOtpError('Network error. Please try again.');
      setOtpStep('error');
    }
  }

  async function handleVerifyOtp() {
    if (!otpCode || otpCode?.length !== 6) {
      setOtpError('Please enter the 6-digit code.');
      return;
    }
    setOtpStep('verifying');
    setOtpError('');
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke('send-email-otp', {
        body: { email: value?.trim(), action: 'verify', otp: otpCode }
      });
      if (fnError || data?.error) {
        setOtpError(fnError?.message || data?.error || 'Invalid code. Please try again.');
        setOtpStep('sent');
      } else if (data?.verified) {
        setOtpStep('verified');
        setOtpError('');
        onVerified(value?.trim());
      }
    } catch (err) {
      setOtpError('Network error. Please try again.');
      setOtpStep('sent');
    }
  }

  return (
    <div>
      <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
        Email Address
        <span className="text-destructive ml-1">*</span>
        {isVerified && (
          <span className="ml-2 inline-flex items-center gap-1 text-xs text-success font-normal">
            <Icon name="CheckCircle2" size={12} color="var(--color-success)" />
            {isUnchanged ? 'Current email' : 'Verified'}
          </span>
        )}
      </label>
      <input
        id="email"
        name="email"
        type="email"
        placeholder="e.g. jane.doe@email.com"
        value={value || ''}
        onChange={handleEmailChange}
        disabled={isVerified && !isUnchanged}
        maxLength={150}
        className={`w-full px-3 py-2 rounded-md border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors disabled:opacity-60 ${
          error ? 'border-destructive focus:ring-destructive' : isVerified ? 'border-success' : 'border-input'
        }`}
      />
      {!isUnchanged && !isVerified && (
        <p className="mt-1 text-xs text-amber-600 flex items-center gap-1">
          <Icon name="AlertTriangle" size={12} />
          New email must be verified before saving
        </p>
      )}
      {/* Send OTP button */}
      {!isVerified && !isUnchanged && (otpStep === 'idle' || otpStep === 'error') && (
        <div className="mt-2">
          <button
            type="button"
            onClick={handleSendOtp}
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-medium disabled:opacity-50"
          >
            <Icon name="Mail" size={14} />
            Send verification code
          </button>
          {otpError && (
            <p className="mt-1 text-xs text-destructive flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {otpError}
            </p>
          )}
        </div>
      )}
      {otpStep === 'sending' && (
        <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
          <Icon name="Loader" size={12} />
          Sending code to {value?.trim()}…
        </p>
      )}
      {(otpStep === 'sent' || otpStep === 'verifying') && (
        <div className="mt-3 p-3 bg-muted/40 rounded-lg border border-border">
          <p className="text-xs text-muted-foreground mb-2">
            A 6-digit code was sent to <span className="font-medium text-foreground">{value?.trim()}</span>. Enter it below:
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={otpCode}
              onChange={(e) => {
                setOtpCode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6));
                setOtpError('');
              }}
              className="w-32 px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground text-center tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <button
              type="button"
              onClick={handleVerifyOtp}
              disabled={otpStep === 'verifying' || otpCode?.length !== 6}
              className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {otpStep === 'verifying' ? 'Verifying…' : 'Verify'}
            </button>
          </div>
          {otpError && (
            <p className="mt-1.5 text-xs text-destructive flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {otpError}
            </p>
          )}
          <div className="mt-2">
            {cooldown > 0 ? (
              <p className="text-xs text-muted-foreground">Resend in {cooldown}s</p>
            ) : (
              <button type="button" onClick={handleSendOtp} className="text-xs text-primary hover:underline">
                Resend code
              </button>
            )}
          </div>
        </div>
      )}
      {isVerified && !isUnchanged && (
        <div className="mt-2 flex items-center justify-between">
          <p className="text-xs text-success flex items-center gap-1">
            <Icon name="CheckCircle2" size={12} color="var(--color-success)" />
            Email address verified successfully
          </p>
          <button
            type="button"
            onClick={() => {
              setOtpStep('idle');
              setOtpCode('');
              setOtpError('');
              onVerified('');
            }}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Change email
          </button>
        </div>
      )}
      {error && (
        <p className="mt-1 text-xs text-destructive flex items-center gap-1">
          <Icon name="AlertCircle" size={12} />
          {error}
        </p>
      )}
    </div>
  );
}
